import type { PermissionLifecycleCallbacks } from '../callbacks/permission-lifecycle-callbacks.ts'
import type { SubscriptionToken } from '@core/types/subscription-token.ts'
import type { EventTracker } from '@core/events/event-manager.ts'
import type { WebWorkerService } from './notifications/push-worker-service.ts'
import type { PushRegistrationDelegate } from './notifications/abstract-push-registration-service.ts'
import { SubscriberProvider } from '@core/enums/subscriber-provider.ts'
import { Env } from '../env.ts'
import { WebPushRegistrationService } from './notifications/web-push-registration-service.ts'
import { TrackedEventAction } from '@core/events/tracked-event-action.ts'
import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { type DomainConfig, isEventOnlyMode } from '@core/types/domain-config.ts'
import { TrackedEventUtils } from '@core/events/tracked-event.ts'
import { UserStore } from '../storage/user-storage-manager.ts'
import { SubscriberStatus, subscriberStatusToSentenceString } from '../enums/subscriber-status.ts'
import { PermissionResponse, permissionResponseFromNotificationPermission } from '../enums/permission-response.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PermissionEventContext, PermissionService } from './permission-service.ts'
import { Result } from '@core/models/result.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'
import { DebugEventReason } from '../enums/debug-event-reason.ts'
import { PushSDKLifecycleCallbacks } from '../callbacks/push-sdk-lifecycle-callbacks.ts'
import { Globals } from '../globals.ts'
import { PermissionChangeObserver } from './permission-change-observer.ts'

/** @internal */
export type ProviderPermissions = {
    provider: SubscriberProvider.VAPID
    permission: NotificationPermission
    token: PushSubscriptionJSON | null
}

/** @internal */
export class NotificationPermissionService implements PermissionService<ProviderPermissions>, PushRegistrationDelegate {
    public static async _getWebPushSupport(): Promise<Result<true, PNException>> {
        const [isWebPushSupported, reason] = await Env._getIsWebPushSupported()
        return !isWebPushSupported
            ? Result.Err(new PNException(PNExceptionCode.WEB_PUSH_NOT_SUPPORTED, null, reason))
            : Result.Ok(true)
    }

    protected registrationService: WebPushRegistrationService

    private additionalEventData: Record<string, any> = {}
    private additionalEventContext: PermissionEventContext = {}

    private permissionChangeObserver: PermissionChangeObserver | null = null

    private readonly logger: Logger

    public constructor(
        private readonly domain: DomainConfig,
        private readonly workerService: WebWorkerService,
        private readonly eventManager: EventTracker,
        private readonly userStore: UserStore,
        private readonly lifecycleCallbacks?: PermissionLifecycleCallbacks & PushSDKLifecycleCallbacks,
        logger?: Logger,
    ) {
        this.logger = logger ?? TaggedLogger._getInstance('NotificationPermissionService')
        this.registrationService = new WebPushRegistrationService(this.workerService, this, this.domain)
    }

    public get _provider(): SubscriberProvider {
        return Env._pushProvider
    }

    public get _currentHref(): string {
        return Env._location.href
    }

    /**
     * @returns Result.Ok(boolean) or Result.Err(PNException)
     *
     * <b>NOTE:</b>
     * - `Result.Ok(true)`            - Service has successfully finished initialization.
     * - `Result.Ok(false)`           - Service has already been initialized.
     * - `Result.Err(PNException)`    - Unable to initialize due to an environmental or configuration issue.
     */
    public async _init() {
        if (!this.domain.vapid_public_key) {
            return Result.Err(new PNException(PNExceptionCode.WEB_PUSH_CONFIGURATION_MISSING))
        }

        return Result.Ok(true)
    }

    /**
     * Returns the current *browser* permission value from the appropriate registration service.
     * When no value is available the `default` permission set is returned.
     */
    public async _getCurrentPermissions(): Promise<ProviderPermissions> {
        const permissions = await this.registrationService._getCurrentPermissions()

        return {
            provider: this._provider,
            ...permissions,
        } as ProviderPermissions
    }

    /**
     * Attempts to synchronize the cached subscriber state to the current browser permissions.
     *
     * When a mismatch is found the browser permissions are used as the source of truth and the
     * cached subscriber state will be updated accordingly.
     *
     * In some scenarios the browser permissions will dictate that a manual unsubscription or
     * re-subscription has occurred and the appropriate processes should be attempted.
     *
     * eg: When a user manually sets the browser permission dialog toggle or resets their
     *     permission settings.
     */
    public async _synchronize(): Promise<ProviderPermissions | null> {
        // Reconciliation subscribes a browser that is granted but has none, which for a visitor
        // who withdrew consent or asked to be deleted would undo that request without a trace.
        // This guards every caller: the load path, the live permission observer of a tab opened
        // before the withdrawal, and anything added later.
        if (await this.userStore._getIsTrackingHalted()) {
            this.logger.debug('Tracking is halted; skipping notification permission synchronization')
            return null
        }

        if (isEventOnlyMode(this.domain)) {
            this.logger.debug('[Event Only Mode] Skipping notification permission check')
            return null
        }

        const webPushSupport = await NotificationPermissionService._getWebPushSupport()
        if (webPushSupport.isErr()) {
            this.logger.warn('Permission synchronization failed - WebPush is not supported')
            return null
        }

        if (this._provider === SubscriberProvider.VAPID) {
            /**
             * Since WebPush tokens are provided through a service worker subscription
             * we should attempt to wait for the current registrations to signal
             * that they are ready before proceeding.
             */
            await this.workerService._ready(200)
        }

        this.logger.debug('Checking current notification permissions')
        const curr = await this._getCurrentPermissions()
        const subscriberStatus = await this.userStore._getSubscriberStatus()

        // Update the token value for synchronization events.
        if (curr.token) {
            TrackedEventUtils._token = curr.token
        }

        // Reconcile the cached subscriber status against live browser truth (the source
        // of truth). Dispatch is keyed on the browser permission; each handler below
        // reads as a small transition table for that permission value.
        await this._reconcileSubscriberStatus(curr, subscriberStatus)

        // Re-read after reconciliation: branches above can mint or tear down a
        // subscription, so the returned snapshot (and the token used for future events)
        // must reflect post-reconciliation state.
        const synchronizedPerms = await this._getCurrentPermissions()

        /**
         * Update the token value for future tracked events
         * based on the synchronized permission + token values.
         */
        if (synchronizedPerms.permission !== 'granted') {
            TrackedEventUtils._token = null
        } else if (synchronizedPerms.token) {
            TrackedEventUtils._token = synchronizedPerms.token
            this.logger.info('Current token:', synchronizedPerms.token)
        } else {
            this.logger.warn('Unable to resolve current token')
        }

        // Notify the host app only on a genuine permission transition — and from sync, that
        // means a change *across loads* (e.g. the user toggled the browser site setting
        // between visits). Comparing the two within-sync reads of the permission is vacuous
        // (reconciliation never changes the live browser permission), so compare against the
        // persisted last-known value instead. The prompt path (`_requestPermission`) still
        // handles in-session changes.
        const lastPermission = await this.userStore._getLastNotificationPermission()
        if (lastPermission && lastPermission !== synchronizedPerms.permission) {
            this.lifecycleCallbacks?.onPushSDKDidReceivePermissionStatusChange?.(
                permissionResponseFromNotificationPermission(synchronizedPerms.permission),
            )
        }
        await this.userStore._setLastNotificationPermission(synchronizedPerms.permission)

        return synchronizedPerms
    }

    /**
     * Begins watching for out-of-band browser permission changes (e.g. the user toggling the
     * site notification setting) and reconciles them live via {@link _synchronize} — closing the
     * gap where a revoke/grant was otherwise only noticed on the next page load. No-op where the
     * Permissions API is unavailable; those browsers continue to reconcile at load time only.
     *
     * @param onChange optional callback invoked after each reconciled change — used to re-evaluate
     *        page-view state so an in-session subscribe still emits a view (parity with the legacy poll).
     */
    public _watch(onChange?: () => void | Promise<void>): void {
        if (isEventOnlyMode(this.domain)) {
            return
        }

        if (!PermissionChangeObserver._isSupported) {
            this.logger.debug('Permissions API change events unavailable; permission changes reconciled at next load')
            return
        }

        this.permissionChangeObserver ??= new PermissionChangeObserver(async () => {
            this.logger.debug('Notification permission changed — re-synchronizing')
            await this._synchronize()
            await onChange?.()
        }, this.logger)

        void this.permissionChangeObserver._start()
    }

    public _stopWatching(): void {
        this.permissionChangeObserver?._stop()
        // Dropped rather than kept: the prompt flow restarts the observer it finds when it finishes.
        this.permissionChangeObserver = null
    }

    /**
     * Reconciles the cached {@link SubscriberStatus} against the live browser permission.
     * The browser is the source of truth: when the two disagree we update the cache and,
     * where required, recover a working subscription or tear down a dead one.
     */
    protected async _reconcileSubscriberStatus(
        curr: ProviderPermissions,
        subscriberStatus: SubscriberStatus,
    ): Promise<void> {
        switch (curr.permission) {
            case 'granted':
                return this._reconcileGrantedPermission(subscriberStatus, curr.token)
            case 'denied':
                return this._reconcileDeniedPermission(subscriberStatus)
            case 'default':
                return this._reconcileDefaultPermission(subscriberStatus)
            default:
                this.logger.warn('Unhandled permission and subscription status combination:', {
                    provider: curr.provider,
                    permission: curr.permission,
                    token: curr.token,
                    subscriptionStatus: subscriberStatus,
                })
        }
    }

    /**
     * Browser permission is `granted`. Three sub-cases:
     * - healthy subscribed (token present): verify the endpoint is still valid for the
     *   domain's current VAPID key (NS-2557) and re-report if the subscription silently
     *   drifted from what the server last received (NS-2569);
     * - subscribed but token not yet surfaced: a cold/still-activating service worker can
     *   read as having no subscription — wait for it to surface before concluding the
     *   subscription is lost, so we don't re-subscribe over a live endpoint (NS-2554);
     * - granted out-of-band (any non-subscribed cached status): silently subscribe and
     *   promote the cached status on success.
     */
    protected async _reconcileGrantedPermission(
        subscriberStatus: SubscriberStatus,
        token: PushSubscriptionJSON | null,
    ): Promise<void> {
        if (subscriberStatus === SubscriberStatus.SUBSCRIBED && token) {
            // NS-2557: a healthy-looking subscribed user may hold an endpoint minted under
            // a now-rotated VAPID applicationServerKey, which silently fails delivery. The
            // browser does not fire pushsubscriptionchange for a publisher-side key change,
            // so detect the mismatch here and re-subscribe under the current key.
            if (await this.workerService._hasStaleVapidSubscription()) {
                this.logger.info('VAPID applicationServerKey rotated; re-subscribing under the current key')
                await this._attemptSilentSubscription()
                return
            }

            // NS-2569: a granted + subscribed user can hold a browser subscription that was
            // silently rotated (new endpoint/keys, same VAPID) without a `pushsubscriptionchange`,
            // leaving the server with stale, undecryptable subscription_data. Re-report on drift.
            await this._reconcileReportedSubscription(token)
            this.logger.info('Current notification permissions:', subscriberStatusToSentenceString(subscriberStatus))
            return
        }

        if (subscriberStatus === SubscriberStatus.SUBSCRIBED && !token) {
            /**
             * Cached as subscribed, but the live token didn't surface — the dangerous case.
             * A cold service worker frequently fails to surface getSubscription() within the
             * brief `_ready(200)` window in {@link _synchronize}, so a single read can report
             * an existing subscription as missing. Re-subscribing in that window mints a NEW
             * endpoint and orphans the live one — growing the pool of dead endpoints (which
             * drags impression rate down) and leaving two registrations per origin. So give
             * the existing subscription a real budget to surface before concluding it's gone;
             * only re-subscribe when it is genuinely absent, and never emit a `subscribed` or
             * mint an endpoint for a subscription that simply hadn't surfaced yet (NS-2554).
             */
            const existingToken = await this._resolveExistingSubscriptionToken()
            if (existingToken) {
                this.logger.debug('Existing subscription surfaced after service worker warm-up; skipping re-subscribe')

                // Adopt the resolved token for subsequent tracked events without
                // emitting a new subscription event or creating a new endpoint.
                TrackedEventUtils._token = existingToken

                // This cold-but-subscribed cohort hits this branch on every load; if its
                // endpoint silently rotated, the server holds stale, undecryptable
                // subscription_data and would otherwise never heal. Re-report on drift here
                // (mirroring the subscribed-with-token case above) so these users self-heal.
                await this._reconcileReportedSubscription(existingToken)
                return
            }

            this.logger.verbose('Subscription genuinely missing for granted permissions; recovering')
            await this._attemptSilentSubscription()
            return
        }

        // Cached status is DENIED / DISMISSED / NOT_DETERMINED but the browser now reports
        // granted — the user enabled us out-of-band (or local status was reset/migrated).
        //
        // Prefer adopting an already-live endpoint over minting a new one. A granted user
        // with a non-subscribed cached status very often still holds a working browser
        // subscription (the status was cleared/migrated, not the subscription). Re-subscribing
        // would mint a duplicate and emit a spurious `subscribed` for an endpoint that
        // already exists. Resolve the existing subscription first (cold-worker budget); if
        // present, adopt it, promote the cached status, and re-report only on drift via
        // `subscription_change` — no new `subscribed`. Only when genuinely absent do we
        // re-subscribe (which emits `subscribed`).
        //
        // This is safe against the backend losing the subscriber: server-side, the push
        // endpoint is a first-class matching identifier, so a `subscription_change` re-binds
        // and refreshes the subscriber by endpoint, or creates the subscriber when the
        // endpoint is genuinely new. Registration is endpoint-driven, not dependent on the
        // `subscribed` event — and using `subscription_change` here avoids double-counting an
        // endpoint that already exists (NS-2554).
        const existingToken = await this._resolveExistingSubscriptionToken()
        if (existingToken) {
            this.logger.debug('Adopting existing subscription for granted user; promoting status without re-report')
            TrackedEventUtils._token = existingToken
            await this._reconcileReportedSubscription(existingToken)
            await this.userStore._setSubscriberStatus(SubscriberStatus.SUBSCRIBED)
            return
        }

        const succeeded = await this._attemptSilentSubscription()
        if (succeeded) {
            this.logger.debug(
                'Updating subscriber status to',
                subscriberStatusToSentenceString(SubscriberStatus.SUBSCRIBED),
            )
            await this.userStore._setSubscriberStatus(SubscriberStatus.SUBSCRIBED)
        }
    }

    /**
     * Browser permission is `denied`. Mirror it into the cache. A user who was SUBSCRIBED
     * and is now denied flipped the toggle in browser settings, so tear down the dead
     * subscription too — but a non-subscribed status simply becomes DENIED without being
     * treated as a manual-unsubscribe event.
     */
    protected async _reconcileDeniedPermission(subscriberStatus: SubscriberStatus): Promise<void> {
        switch (subscriberStatus) {
            case SubscriberStatus.DENIED:
                // Already in sync.
                this.logger.info('Current notification permissions:', subscriberStatusToSentenceString(subscriberStatus))
                return

            case SubscriberStatus.SUBSCRIBED:
                // User manually set permissions to denied via browser settings.
                await this.userStore._setSubscriberStatus(SubscriberStatus.DENIED)
                await this._revokePermission()
                return

            case SubscriberStatus.DISMISSED:
            case SubscriberStatus.NOT_DETERMINED:
            default:
                this.logger.debug(
                    'Updating subscriber status to',
                    subscriberStatusToSentenceString(SubscriberStatus.DENIED),
                )
                await this.userStore._setSubscriberStatus(SubscriberStatus.DENIED)
                return
        }
    }

    /**
     * Browser permission is `default` (never decided, or reset/cleared). Reconcile a stale
     * cached terminal status back toward not-determined, and tear down a subscription the
     * user cleared via browser settings. A genuinely-dismissed (TTL-bound) status is left
     * untouched so the dismissal window is respected.
     */
    protected async _reconcileDefaultPermission(subscriberStatus: SubscriberStatus): Promise<void> {
        switch (subscriberStatus) {
            case SubscriberStatus.NOT_DETERMINED:
                // Already in sync.
                this.logger.info('Current notification permissions:', subscriberStatusToSentenceString(subscriberStatus))
                return

            case SubscriberStatus.DISMISSED:
                // Genuinely dismissed — leave the dismissed status (and its TTL) in place.
                this.logger.info(
                    `User has recently ${subscriberStatusToSentenceString(SubscriberStatus.DISMISSED)} permissions`,
                )
                return

            case SubscriberStatus.SUBSCRIBED:
                // User manually reset/cleared permissions via browser settings.
                this.logger.debug(
                    'Updating subscriber status to',
                    subscriberStatusToSentenceString(SubscriberStatus.NOT_DETERMINED),
                )
                await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
                await this._revokePermission()
                return

            case SubscriberStatus.DENIED:
            default:
                // A previously-denied user reset their browser permissions.
                this.logger.debug(
                    'Updating subscriber status to',
                    subscriberStatusToSentenceString(SubscriberStatus.NOT_DETERMINED),
                )
                await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
                return
        }
    }

    /**
     * Attempts to request browser Notification permissions
     * if the current permissions value is not determined or "default".
     */
    public async _requestPermission(dismissalExpirationSeconds?: number): Promise<ProviderPermissions> {
        dismissalExpirationSeconds ??= Globals._defaultDismissedStatusExpirationSeconds

        const curr = await this._getCurrentPermissions()

        if (curr.permission === 'denied') {
            this.logger.info('Permission already denied')
            return curr
        }

        if (curr.permission === 'granted') {
            this.logger.info('Permission already granted')
            return curr
        }

        // Pause the PermissionChangeObserver for the duration of the prompt + subscribe flow.
        // When the user grants permission, BOTH the requestPermission promise resolution AND the
        // Permissions API `onchange` event fire near-simultaneously. Without pausing, the
        // observer's _synchronize enters _reconcileGrantedPermission before the prompt flow's
        // _subscribe() completes, doesn't see a token, falls through to _attemptSilentSubscription,
        // and emits a duplicate `subscribed` event without the prompt attribution data.
        this.permissionChangeObserver?._stop()

        let permissionRes: Omit<ProviderPermissions, 'provider'> | null | undefined
        try {
            // Thread the already-resolved permissions through so the registration service
            // does not re-run navigator.permissions.query() (NS-2558). A copy is passed so
            // downstream mutation of `permission` cannot affect the `curr` compared below.
            permissionRes = await this.registrationService?._requestNotificationPermission({
                permission: curr.permission,
                token: curr.token,
            })
        } finally {
            void this.permissionChangeObserver?._start()
        }

        const next = {
            ...permissionRes,
            provider: this._provider,
        } as ProviderPermissions

        if (next.permission !== curr.permission || next.token !== curr.token) {
            this.lifecycleCallbacks?.onPushSDKDidReceivePermissionStatusChange?.(
                permissionResponseFromNotificationPermission(next.permission),
            )
        }

        return next
    }

    /**
     * If the current provider is {@link SubscriberProvider.VAPID} an attempt to perform
     * an unsubscribe will be triggered. In the event of a successful removal of the
     * current subscription a {@link TrackedEventAction.manualUnsubscribed} event
     * will be seeded.
     */
    public async _revokePermission(): Promise<void> {
        const succeeded = await this.workerService._unsubscribe()
        if (succeeded) {
            this.eventManager._track(TrackedEventAction.MANUAL_UNSUBSCRIBED)
        }
    }

    /**
     * Performs a subscription without prompting the user for permissions.
     *
     * Primary use case is when permissions have already been granted,
     * either manually or due to a migration, and the SDK simply wants
     * to ensure the proper service worker and a working subscription
     * is created.
     */
    protected async _attemptSilentSubscription(): Promise<boolean> {
        this.logger.verbose('Attempting silent subscription')

        const subRes = await this.workerService._subscribe()
        if (subRes.isErr()) {
            this.logger.warn(subRes.getErr())
            this.eventManager._track(subRes.getErr())
            return false
        }

        let token: SubscriptionToken | null = null
        if (subRes.isOk()) {
            const pushSubscription = subRes.unwrap()
            token = pushSubscription ? pushSubscription.toJSON() : null
        }

        if (token) {
            this.logger.debug('Silent subscription succeeded:', token)

            // Assign resolved token value for future tracked events.
            TrackedEventUtils._token = token

            // No prompt associated with these subscription events.
            this.eventManager._track(await this._getBaseSubscriptionEvent())

            await this._recordReportedSubscription(token)

            return true
        }

        this.logger.debug('Silent subscription failed')
        return false
    }

    /**
     * Resolves an already-subscribed user's existing push subscription token, or
     * `null` if no subscription is present once the service worker is active.
     *
     * The initial `_ready(200)` check in {@link _synchronize} is intentionally short
     * so first-time visitors — who have no service worker, and whose
     * `navigator.serviceWorker.ready` therefore never resolves — are not blocked. But
     * for a user cached as SUBSCRIBED a registration *does* exist, so that 200ms read
     * can race a still-activating worker and report the token as missing. Re-subscribing
     * then mints a new endpoint and orphans the live one — growing the pool of dead
     * endpoints and leaving duplicate registrations per origin (NS-2554).
     *
     * So we wait — event-driven, not polled — on `navigator.serviceWorker.ready` (via
     * {@link WebWorkerService._ready}), which resolves the instant the existing worker
     * activates: zero added latency on a warm worker, a bounded wait on a cold one, and
     * a single read afterward. A genuinely-absent subscription (no registration) is
     * short-circuited up front so we don't burn the full budget on a `ready` that would
     * never resolve — it falls straight through to re-subscription recovery.
     */
    protected async _resolveExistingSubscriptionToken(
        readyBudgetMs: number = Globals._coldSubscriptionResolutionBudgetMs,
    ): Promise<SubscriptionToken | null> {
        // With no registration, `serviceWorker.ready` never resolves, so waiting the full
        // cold budget would just stall init-time reconciliation. Bail early.
        const registration = await this.workerService._getRegistration()
        if (!registration) {
            return null
        }

        await this.workerService._ready(readyBudgetMs)

        const subscription = await this.workerService._getSubscription()
        return subscription ? subscription.toJSON() : null
    }

    /**
     * Re-reports the live subscription when it has drifted from what the server last received.
     *
     * The browser can silently rotate a push subscription (new endpoint and/or encryption keys)
     * WITHOUT firing `pushsubscriptionchange`, leaving the server holding stale subscription data.
     * The push backend derives the push-body encryption from the stored p256dh/auth, so a stale
     * copy yields a push-service 201 (accepted) with an undecryptable body — the push is silently
     * dropped: no notification shows, no impression is recorded, and the endpoint eventually 410s.
     * Relying on `pushsubscriptionchange` alone is insufficient because browsers do not reliably
     * fire it for silent rotations; re-reporting drift on load is the page-side safety net.
     *
     * Reported as a `subscription_change` (not `subscribed`) so the re-report does not inflate
     * subscription counts. Publisher VAPID-key rotation is handled separately (NS-2557) since it
     * requires a re-subscribe; this path re-reports an already-valid endpoint.
     *
     * Runs on every load; a re-report only fires when the fingerprint actually drifts, so the
     * steady-state cost is nil (NS-2569).
     */
    protected async _reconcileReportedSubscription(token: SubscriptionToken): Promise<void> {
        const fingerprint = NotificationPermissionService._subscriptionFingerprint(token)
        if (!fingerprint) {
            return
        }

        const lastReported = await this.userStore._getReportedSubscriptionFingerprint()
        if (lastReported === fingerprint) {
            return
        }

        this.logger.info('Live subscription differs from last reported value; re-reporting subscription_data')

        // Await the track so the event is durably enqueued+persisted (the event queue persists
        // and retries across sessions) BEFORE advancing the stored fingerprint. Otherwise the
        // fingerprint could move ahead of an event that never made it into the durable queue,
        // and the SDK would believe it had reported — leaving stale, undecryptable subscription
        // data with no self-heal until the next drift.
        // `TrackedEventUtils._token` was assigned the live token earlier in `_synchronize`.
        await this.eventManager._track(TrackedEventAction.TOKEN_REFRESHED, {
            [Globals._tevDataPageUrlKey]: this._currentHref,
        })

        await this.userStore._setReportedSubscriptionFingerprint(fingerprint)
    }

    /**
     * Persists a fingerprint of the subscription we just reported so later loads can detect drift
     * (NS-2569).
     */
    protected async _recordReportedSubscription(token: SubscriptionToken | null): Promise<void> {
        const fingerprint = NotificationPermissionService._subscriptionFingerprint(token)
        if (fingerprint) {
            await this.userStore._setReportedSubscriptionFingerprint(fingerprint)
        }
    }

    /**
     * Stable identity of a push subscription for drift detection: endpoint + encryption keys. A
     * change in any of these means the server's stored subscription_data can no longer encrypt a
     * deliverable push.
     */
    protected static _subscriptionFingerprint(token: SubscriptionToken | null): string | null {
        if (!token || !token.endpoint) {
            return null
        }

        return `${token.endpoint}|${token.keys?.p256dh ?? ''}|${token.keys?.auth ?? ''}`
    }

    public async _getIsEligibleToPrompt(): Promise<Result<true, DebugEventReason>> {
        const isTrackingHalted = await this.userStore._getIsTrackingHalted()
        const isSubscribed = await this.userStore._getIsSubscribed()
        const isUserInSoftUnsubscribedState = await this.userStore._getIsUserInSoftUnsubscribedState()
        const subscriberStatus = await this.userStore._getSubscriberStatus()

        if (isTrackingHalted) return Result.Err(DebugEventReason.USER_DELETED)
        if (isSubscribed) return Result.Err(DebugEventReason.USER_PERMISSION_GRANTED)
        if (isUserInSoftUnsubscribedState) return Result.Err(DebugEventReason.USER_PERMISSION_DENIED)
        if (subscriberStatus === SubscriberStatus.DENIED) return Result.Err(DebugEventReason.USER_PERMISSION_DENIED)
        if (subscriberStatus === SubscriberStatus.DISMISSED)
            return Result.Err(DebugEventReason.USER_PERMISSION_DISMISSED)

        return Result.Ok(true)
    }

    public _setPermissionEventContext(context: PermissionEventContext): void {
        this.additionalEventContext = context
    }

    public _resetPermissionEventContext(): void {
        this.additionalEventContext = {}
    }

    public _setAdditionalPermissionEventData(data: Record<string, any>): void {
        this.additionalEventData = data
    }

    // region RegistrationDelegate Methods
    /**
     * Seeds a {@link TrackedEventAction.permissionShown} event.
     */
    public async _onPushPermissionRequested(): Promise<void> {
        this.eventManager._track(TrackedEventAction.PERMISSION_SHOWN, {
            ...this.additionalEventData,
        })

        this.lifecycleCallbacks?.onPushSDKDidRequestNotificationPermissions?.()
    }

    /**
     * Updates the cached subscriber status to {@link SubscriberStatus.DENIED}
     * and forwards DENIED {@link PermissionResponse} to
     * the {@link onPushSDKDidReceivePermissionResponse} {@link PermissionLifecycleCallbacks}.
     *
     * Seeds a {@link TrackedEventAction.permissionDenied} event.
     */
    public async _onPushPermissionDenied(): Promise<void> {
        await this.userStore._setSubscriberStatus(SubscriberStatus.DENIED)

        this.lifecycleCallbacks?.onPushSDKDidReceivePermissionResponse?.(PermissionResponse.DENIED)

        this.eventManager._track(TrackedEventAction.PERMISSION_DENIED, {
            ...this.additionalEventData,
        })

        this._resetPermissionEventContext()
    }

    /**
     * Updates the cached subscriber status to {@link SubscriberStatus.DISMISSED}
     * and forwards DISMISSED {@link PermissionResponse} to
     * the {@link onPushSDKDidReceivePermissionResponse} {@link PermissionLifecycleCallbacks}.
     *
     * Seeds a {@link TrackedEventAction.permissionDismissed} event.
     */
    public async _onPushPermissionDismissed(): Promise<void> {
        const dismissalExpirationSeconds =
            this.additionalEventContext.dismissalExpirationSeconds ?? Globals._defaultDismissedStatusExpirationSeconds

        if (dismissalExpirationSeconds === 0) {
            this.logger.debug(`Setting subscriber status to NOT_DETERMINED`)
            await this.userStore._setSubscriberStatus(SubscriberStatus.NOT_DETERMINED)
        } else {
            this.logger.debug(`Setting subscriber status to DISMISSED for ${dismissalExpirationSeconds}s`)
            await this.userStore._setSubscriberStatusDismissed(dismissalExpirationSeconds)
        }

        this.lifecycleCallbacks?.onPushSDKDidReceivePermissionResponse?.(PermissionResponse.DISMISSED)

        this.eventManager._track(TrackedEventAction.PERMISSION_DISMISSED, {
            ...this.additionalEventData,
        })

        this._resetPermissionEventContext()
    }

    /**
     * Updates the cached subscriber status to {@link SubscriberStatus.SUBSCRIBED}
     * and forwards GRANTED {@link PermissionResponse} to
     * the {@link onPushSDKDidReceivePermissionResponse} {@link PermissionLifecycleCallbacks}.
     *
     * Seeds a {@link TrackedEventAction.permissionAccepted} event.
     *
     * <b>Note:</b> Setting the status to subscribed in the granted handler
     * will ensure that if a subscription failure occurs the next page load
     * will trigger an attempt to silently resubscribe.
     */
    public async _onPushPermissionGranted(): Promise<void> {
        await this.userStore._setSubscriberStatus(SubscriberStatus.SUBSCRIBED)

        this.lifecycleCallbacks?.onPushSDKDidReceivePermissionResponse?.(PermissionResponse.GRANTED)

        this.eventManager._track(TrackedEventAction.PERMISSION_ACCEPTED, {
            ...this.additionalEventData,
        })
    }

    /**
     * Updates the {@link TrackedEventUtils._token} with the granted {@link SubscriptionToken}
     * and forwards the current {@link SubscriberProvider} and token to
     * the {@link onPushSDKDidRegisterForRemoteNotificationsWithDeviceToken}
     * {@link PermissionLifecycleCallbacks}.
     *
     * Seeds a {@link TrackedEventAction.tokenReceived} event.
     */
    public async _onPushSubscriptionSucceeded(token: SubscriptionToken): Promise<void> {
        TrackedEventUtils._token = token

        this.lifecycleCallbacks?.onPushSDKDidRegisterForRemoteNotificationsWithDeviceToken?.(token)

        const subEvent = await this._getBaseSubscriptionEvent()
        subEvent.data = {
            ...this.additionalEventData,
            ...subEvent.data,
        }

        this.eventManager._track(subEvent)

        await this._recordReportedSubscription(token)

        this._resetPermissionEventContext()
    }

    protected async _getBaseSubscriptionEvent() {
        return TrackedEventUtils._build(TrackedEventAction.TOKEN_RECEIVED, {
            [Globals._tevDataPageUrlKey]: this._currentHref,
            [Globals._tevSubscribedUrlKey]: this._currentHref,
        })
    }

    /**
     * Removes any possible {@link TrackedEventUtils._token} value
     * and forwards any possible registration error to
     * the {@link onPushSDKDidFailToRegisterForRemoteNotificationsWithError}
     * {@link PermissionLifecycleCallbacks}.
     *
     * If an error event is passed a {@link TrackedEventAction.error} event
     * will also be seeded for tracking and debugging purposes.
     */
    public async _onPushSubscriptionFailed(error?: Error): Promise<void> {
        TrackedEventUtils._token = null

        this.logger.info(`event:${TrackedEventAction.ERROR}`, error)
        this.lifecycleCallbacks?.onPushSDKDidFailToRegisterForRemoteNotificationsWithError?.(
            new PNException(PNExceptionCode.WEB_PUSH_SUBSCRIPTION_FAILED, error),
        )

        if (error) {
            this.eventManager._track(error)
        }

        this._resetPermissionEventContext()
    }
    // endregion
}
